'use client';

import { useEffect, useRef } from 'react';

/**
 * Интерактивная сеть узлов, декор для страниц ошибок продуктов платформы.
 *
 * Общий компонент, не завязанный на бренд конкретного продукта: цвета берёт
 * из CSS-переменных активной темы (`ais-theme`, `dh-theme` и так далее), а
 * не зашивает свои. Используется на страницах 404/500 AiSearch и DataHunter.
 *
 * Не иллюстрация конкретной нейросети, а атмосферная развлекуха: точки
 * плавают, соединяются линиями с ближайшими соседями и тянутся к курсору,
 * будто сеть «замечает» человека. Ничего не грузит и не выполняет —
 * страница ошибки не место для настоящей модели, только для намёка на неё.
 *
 * Canvas, а не DOM-элементы на узел: сотня точек и линий между ближайшими
 * парами через `<div>` заметно грузили бы layout при каждом кадре.
 *
 * Уважает `prefers-reduced-motion`: тем, кто просил систему не двигать
 * интерфейс, рисуем статичный кадр без анимации.
 */
export function NeuralPlayground({ className }: { className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let width = 0;
    let height = 0;
    let dpr = 1;

    interface Node {
      x: number;
      y: number;
      vx: number;
      vy: number;
    }

    // Плотность считаем от площади, а не от фиксированного числа: на
    // телефонном экране сотня узлов слиплась бы в кашу.
    const NODE_COUNT_BASE = 42;
    let nodes: Node[] = [];

    const pointer = { x: -9999, y: -9999, active: false };

    function resize() {
      if (!canvas) return;
      const rect = canvas.parentElement?.getBoundingClientRect();
      width = rect?.width ?? window.innerWidth;
      height = rect?.height ?? 420;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${String(width)}px`;
      canvas.style.height = `${String(height)}px`;
      ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);

      const area = width * height;
      const count = Math.max(18, Math.min(70, Math.round((area / (1280 * 420)) * NODE_COUNT_BASE)));
      nodes = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
      }));
    }

    resize();
    window.addEventListener('resize', resize);

    function onPointerMove(event: PointerEvent) {
      const rect = canvas?.getBoundingClientRect();
      if (!rect) return;
      pointer.x = event.clientX - rect.left;
      pointer.y = event.clientY - rect.top;
      pointer.active = true;
    }
    function onPointerLeave() {
      pointer.active = false;
    }

    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerleave', onPointerLeave);

    // Цвета читаем из CSS-переменных темы продукта, а не хардкодим: страница
    // рисуется и в тёмной, и в светлой теме, и с палитрой ais-theme.
    const style = getComputedStyle(canvas);
    const lineColor = style.getPropertyValue('--color-accent-500').trim() || '#2f6df6';
    const nodeColor = style.getPropertyValue('--color-brand-500').trim() || '#e6402a';

    let frame = 0;
    const LINK_DIST = 130;
    const POINTER_RADIUS = 160;

    function draw() {
      if (!ctx) return;
      ctx.clearRect(0, 0, width, height);

      for (const node of nodes) {
        if (!reduceMotion) {
          node.x += node.vx;
          node.y += node.vy;

          if (node.x < 0 || node.x > width) node.vx *= -1;
          if (node.y < 0 || node.y > height) node.vy *= -1;
          node.x = Math.max(0, Math.min(width, node.x));
          node.y = Math.max(0, Math.min(height, node.y));

          // Лёгкое притяжение к курсору: сеть будто оборачивается на человека,
          // но не липнет к нему намертво — коэффициент маленький.
          if (pointer.active) {
            const dx = pointer.x - node.x;
            const dy = pointer.y - node.y;
            const dist = Math.hypot(dx, dy);
            if (dist < POINTER_RADIUS && dist > 0.01) {
              node.vx += (dx / dist) * 0.006;
              node.vy += (dy / dist) * 0.006;
            }
          }

          // Трение: без него притяжение к курсору разгоняло бы узлы бесконечно.
          node.vx *= 0.99;
          node.vy *= 0.99;
        }
      }

      // Связи между близкими узлами, ярче для тех, что ближе.
      for (let i = 0; i < nodes.length; i++) {
        const nodeA = nodes[i];
        if (!nodeA) continue;

        for (let j = i + 1; j < nodes.length; j++) {
          const nodeB = nodes[j];
          if (!nodeB) continue;

          const dist = Math.hypot(nodeA.x - nodeB.x, nodeA.y - nodeB.y);
          if (dist < LINK_DIST) {
            ctx.strokeStyle = lineColor;
            ctx.globalAlpha = (1 - dist / LINK_DIST) * 0.35;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(nodeA.x, nodeA.y);
            ctx.lineTo(nodeB.x, nodeB.y);
            ctx.stroke();
          }
        }

        // Связь узла с курсором: видно, что сеть реагирует именно на него.
        if (pointer.active) {
          const dist = Math.hypot(nodeA.x - pointer.x, nodeA.y - pointer.y);
          if (dist < POINTER_RADIUS) {
            ctx.strokeStyle = nodeColor;
            ctx.globalAlpha = (1 - dist / POINTER_RADIUS) * 0.5;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(nodeA.x, nodeA.y);
            ctx.lineTo(pointer.x, pointer.y);
            ctx.stroke();
          }
        }
      }

      ctx.globalAlpha = 1;
      for (const node of nodes) {
        ctx.fillStyle = nodeColor;
        ctx.beginPath();
        ctx.arc(node.x, node.y, 2.2, 0, Math.PI * 2);
        ctx.fill();
      }

      frame = reduceMotion ? frame : requestAnimationFrame(draw);
    }

    draw();

    return () => {
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerleave', onPointerLeave);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return <canvas ref={canvasRef} className={className} aria-hidden="true" role="presentation" />;
}
